import React, {useMemo, useState} from 'react'

import {useQuery} from '@tanstack/react-query'
import {useTranslation} from 'react-i18next'
import {Modal, ModalBody, ModalHeader} from 'reactstrap'

import {GetStockHistory} from '../../../infra/requests/LogisticsRequests'
import {GetTranslationNameFromArray} from '../../../infra/translations/AvailableTranslations'
import Table from '../../Common/Table/index'
import {useWarehouseAndMovementData} from '../../Hooks/useWarehouseAndMovementData'
import {Alert} from '../Alerts/Alert'
import ButtonIcon from '../Buttons/ButtonIcon'
import {createStockHistoryModalColumns} from './stock-hitory-modal/stockHistoryModalColumnConfig'

const MAXIMUM_EXPORT_LINES = 1000

//FIXME when active language is set up
const TEMP_LANGUAGE_ID = 2

const StockHistoryModal = ({isOpened, toggle, item}) => {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(12)
  const [columnFilters, setColumnFilters] = useState({
    pcwMovementId: '',
    date: [],
    movementType: [],
    warehouseType: [],
    users: []
  })
  const [alertVisible, setAlertVisible] = useState(false)
  const {t} = useTranslation()

  const {movementTypes, warehouses, usersWithMovements} =
    useWarehouseAndMovementData()

  {
    /*
   FIXME temporary solution to avoid multiple retriggers of getStockHistory
   This should be fixed when Table component is fixed and refactored 
  */
  }
  const stableFilters = useMemo(() => {
    return {
      pcwMovementId: columnFilters?.pcwMovementId || '',
      date: columnFilters?.date || [],
      movementType: columnFilters?.movementType || [],
      warehouseType: columnFilters?.warehouseType || [],
      users: columnFilters?.users || []
    }
  }, [
    columnFilters?.pcwMovementId,
    JSON.stringify(columnFilters?.date),
    JSON.stringify(columnFilters?.movementType),
    JSON.stringify(columnFilters?.warehouseType),
    JSON.stringify(columnFilters?.users)
  ])

  const {data: stockHistoryRawData, isFetching} = useQuery({
    queryKey: ['stockHistoryModal', pageSize, page, stableFilters],
    queryFn: () =>
      GetStockHistory({
        ...item,
        exportxls: false,
        itemsperpage: pageSize,
        page: page,
        pcwMovementId: columnFilters?.pcwMovementId,
        datefrom: columnFilters?.date[0] ? columnFilters?.date[0] : undefined,
        dateto: columnFilters?.date[1] ? columnFilters?.date[1] : undefined,
        MovementTypeIds: columnFilters?.movementType,
        WarehouseIds: columnFilters?.warehouseType,
        CreatedBy: columnFilters?.users
      })
  })

  const stockHistoryData = stockHistoryRawData?.data

  const handleDownloadModal = async () => {
    try {
      const authToken = localStorage.getItem('authToken')

      const response = await fetch(
        `${window.env.REACT_APP_API}/Logistics/GetStockHistory`,
        {
          method: 'POST',
          body: JSON.stringify({
            productid: 17,
            sizeid: 25,
            colorid: 21,
            productwidthid: 1,
            exportxls: true,
            itemsperpage: pageSize,
            page: page,
            pcwMovementId: columnFilters?.pcwMovementId,
            datefrom: columnFilters?.date[0]
              ? columnFilters?.date[0]
              : undefined,
            dateto: columnFilters?.date[1] ? columnFilters?.date[1] : undefined,
            MovementTypeIds: columnFilters?.movementType,
            WarehouseIds: columnFilters?.warehouseType,
            CreatedBy: columnFilters?.users
          }),
          headers: {
            Authorization: `Bearer ${authToken}`,
            'Content-Type': 'application/json'
          }
        }
      )

      if (response.status === 200) {
        const blob = await response.blob()
        const reader = new FileReader()

        reader.onload = event => {
          const csvContent = event.target.result
          const rowCount = csvContent.split('\n').length

          if (rowCount <= MAXIMUM_EXPORT_LINES) {
            const url = window.URL.createObjectURL(blob)
            const a = document.createElement('a')
            a.style.display = 'none'
            a.href = url
            a.download = 'tts.xls'
            document.body.appendChild(a)
            a.click()
            window.URL.revokeObjectURL(url)
          } else {
            setAlertVisible(true) // Exibe o alerta se houver mais de 1.000 linhas
            setTimeout(() => {
              setAlertVisible(false)
            }, 5000)
          }
        }

        reader.readAsText(blob)
      } else {
        console.log(`Error: ${response.statusText}`)
      }
    } catch (error) {
      console.log(error)
    }
  }

  return (
    <>
      <Modal
        className="stock-history-modal"
        size="xl"
        isOpen={isOpened}
        toggle={() => {
          toggle()
        }}
        centered>
        <ModalHeader className="modal-title w-100">
          <div className="w-100 d-flex justify-content-between align-items-center custom-height-16">
            <div>{t('STOCK_HISTORY')}</div>
            <div
              className="cursor-pointer"
              onClick={() => {
                toggle(false)
              }}>
              <ButtonIcon type="noBorder" icon="xIcon" color="black" />
            </div>
          </div>
        </ModalHeader>

        <ModalBody className="stock-history-modal-body">
          <div className="d-flex justify-content-between align-items-center modal-body-padding">
            <div>
              <div className="heading_3">
                {GetTranslationNameFromArray(
                  item?.productTranslation,
                  TEMP_LANGUAGE_ID
                )}
              </div>
              <div className="heading_6 stock-history-modal-header-custom-color">
                {item?.reference}
              </div>
            </div>
            <ButtonIcon
              type="secondary"
              icon="xls"
              size={[21, 21]}
              onClick={() => handleDownloadModal()}
            />
          </div>
          <div className="d-flex justify-content-start flex-column stock-history-modal-table-wrapper">
            {alertVisible && (
              <Alert
                color="danger"
                text="Não é possivel exportação total dos dados."
                additional={false}
                borderless={true}
                heading="Aviso"
              />
            )}
            <Table
              totalItems={stockHistoryData?.totalNumberOfRows}
              currentPage={page}
              setCurrentPage={setPage}
              rowsPerPage={pageSize}
              setRowsPerPage={setPageSize}
              tableColumns={createStockHistoryModalColumns(
                movementTypes,
                warehouses,
                usersWithMovements,
                stockHistoryData,
                pageSize
              )}
              loading={isFetching}
              tableInfo={stockHistoryData?.rows}
              small
              columnFilters={columnFilters}
              setColumnFilters={setColumnFilters}
              paginationOptions={[
                {label: '12', value: 12},
                {label: '24', value: 24},
                {label: '48', value: 48}
              ]}
            />
          </div>
        </ModalBody>
      </Modal>
    </>
  )
}

export default StockHistoryModal
